/*
 * Copyright (c) 2025. Jellybean B.V.
 * Jellybean B.V. Apeldoorn heeft een licentie verleend aan
 * Spin in het Web B.V. Apeldoorn voor het gebruik van deze software-
 * onderdelen in projecten voor derden. U heeft géén toestemming
 * tot inzage of wijziging in de (bron)code van deze onderdelen.
 */

/*
* sihwtijdveld.ts
* 13-3-2020 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Component, forwardRef, HostBinding, Input} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import {Sihwlog, Sihwlogger} from '../../sihwlog/sihwlog';

@Component({
  selector: 'sihwtijdveld',
  templateUrl: './sihwtijdveld.html',
  styleUrls: ['./sihwtijdveld.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: SihwTijdveld, multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => SihwTijdveld), multi: true} //doorgeven valid state
  ],
  standalone: false
})
export class SihwTijdveld implements ControlValueAccessor, Validator {
  private log: Sihwlogger;
  ctrlval: string | null = null; //onze ngmodel var
  private tijd: { uur: number, min: number } | null = null; //onze interne representatie
  private valid: boolean = true; //vinden wij het valid
  private verandering: boolean = false; //is er een change geweest? Gebruikt bij blur
  private meldGewijzigd: (_: any) => void = (_) => {}; //controlvalueaccessor
  onTouched: () => void = () => {
  }; //controlvalueaccessor


  @Input('disabled') disabled: boolean = false; //de disabled value, en readonly op de host. Dat gebruiken we als alternatief voor disabled, wat we niet goed kunnen zetten
  @HostBinding('attr.readonly') get hostReadonly() {
    return this.disabled ? true : null;
  } //zorg voor verwijderen readonly als niet nodig

  //copy props
  @Input() placeholder: string = '';
  @Input() size: Number = 5; //er moet een default zijn
  @Input() voorloopnul: boolean = false; //uren altijd 2 decimalen?

  constructor(sihwlog: Sihwlog) {
    this.log = sihwlog.logger();
  }

  ///////////////////////////////////////// CONTROLVALUEACCESSOR //////////////////
  /**
   * Nieuwe waarde wordt aangeboden
   * @param obj
   */
  writeValue(obj: any): void {
    //de waarde komt als string binnen
    this.ctrlval = obj; //eerst maar eens
    this.parseVal(); //parse deze waarde
    if ( this.valid) {
      this.ctrlval = this.toString(); //aanpassen aan de gewenste representatie
    }
    this.log.debug(`sihwtijdveld set value`, obj, this.tijd);
  }


  registerOnChange(fn: any): void {
    this.meldGewijzigd = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.log.debug(`sihwtijdveld setDisabled`, isDisabled);
    this.disabled = isDisabled;
  }

  /////////////////////////////// Validator ////////////////////////////////
  validate(control: AbstractControl): ValidationErrors | null {
    //dit hebben we al gedaan
    this.log.debug(`sihwtijdveld validate`,this.valid);
    return this.valid ? null :  {sihwtijdveld: true};
  }

  //////////////////////////////// MODEL //////////////////////////////////////////

  /**
   * 1. Binnenkomende waarde: zet de juiste stringrepresentatie in ons veld. That's all. Blijf van de waarde af
   * 2. Gewijzigde waarde: parse en bewaar de juiste stringrepresentatie. Stuur hem naar boven
   * 3. Blur  / Enter: zet de juiste stringrepresentatie als er gewijzigd is
   */

  /**
   * ngOnChange
   */
  valueChanged() {
    //opnieuw parsen
    this.parseVal(); //zet this.tijd en this.valid
    //we melden ook invalid waarden
      let strval = this.valid ? this.toString() : this.ctrlval;
      this.log.debug('sihwtijdveld nieuwe waarde',strval);
      this.meldGewijzigd(strval);
      this.verandering = true; //zie ctrlBlur
  }

  /**
   * Blur: werk de waarde bij en ga door
   */
  ctrlBlur() {
   this.werkWaardeBij();
    //sowieso touched
    this.onTouched();
  }

  //enterkey: ook de waarde bijwerken
  enterkey(event: Event) {
    this.werkWaardeBij();
    //en niet door
    event.preventDefault();
    event.stopPropagation();
  }
  /**
   * Helper bij blur en enterkey
   */
  werkWaardeBij() {
    if (this.verandering && this.valid)
    {
      this.ctrlval = this.toString(); //mooi format
    }
    this.verandering = false;
  }

  /////////////////////////////////////////////////////////////////////////////////
  /**
   * Helper: zet this.tijd {uur:, min:} op uit de ctrlval, en zet this.valid
   */
  parseVal() {
    this.log.debug(`parseVal`,this.ctrlval);
    if (!(this.ctrlval && this.ctrlval.length)) {
      this.valid = true; //we vinden het prima. Required/ empty validators gaan apart
      this.tijd = null;
      return;
    }
    //alleeen cijfers en :
    if ( /[^\d:\s]/.exec(this.ctrlval))
    {
      this.log.debug(`Ongeldige waarde`);
      this.valid = false;
      this.tijd = null;
      return;
    }

    let m;

    let uurbasis: string | undefined, minuutbasis: string | undefined;
    m = /(\d{1,2}):(\d{1,2})/.exec(this.ctrlval);
    if (m) {
      //okee, keurig
      this.log.debug(`sihwtijdveld: match met :`);
      uurbasis = m[1];
      minuutbasis = m[2];
    } else {
      //zonder : ?
      let val = this.ctrlval.replace(':', ''); //eruit
      const m = /(\d{1,4})/.exec(val);
      //ook niet?
      if (!m) {
        this.log.debug(`sihwtijdveld: geen match op 1-4 cijfers`);
        this.valid = false;
        this.tijd = null; //kunnen we niet
        return;
      }
      let basic = m[1]; //de basistijd
      this.log.debug(`sihwtijdveld. Basis`, basic);
      switch (basic.length) {
        case 1:
        case 2:
          //alleen uren
          uurbasis = basic;
          minuutbasis = "0";
          break;
        case 3:
          // u:mm
          uurbasis = basic.substr(0, 1);
          minuutbasis = basic.substr(1, 2);
          break;
        case 4:
          //uu:mm
          uurbasis = basic.substr(0, 2);
          minuutbasis = basic.substr(2, 2);
      }
    }

    let uur: number, minuut: number;
    try {
      uur = parseInt(<string> uurbasis, 10);
      minuut = parseInt(<string> minuutbasis, 10);
      this.log.debug(`Uur: ${uur} Minuut: ${minuut}`);
      //gekke waarden?
      if (isNaN(uur) || isNaN(minuut) || uur > 23 || minuut > 59) {
        this.valid = false;
        this.tijd = null;
        this.log.debug(`Ongeldig`);
        return;
      }
    } catch (e) {
      this.log.warn(e);
      this.valid = false;
      this.tijd = null;
      return;
    }

    this.valid = true;
    this.log.debug(`sihwtijdveld`, uur, minuut);
    this.tijd = {uur: uur, min: minuut};
  }

  /**
   * Zet onze interne tijdrepresentatie om naar een string of null bij null
   */
  private toString(): string | null {
    if (this.tijd === null) {
      return null;
    }
    return `${(this.voorloopnul && (this.tijd.uur < 10)) ? '0' : ''}${this.tijd.uur}:${this.tijd.min < 10 ? '0' : ''}${this.tijd.min}`;
  }
}
